/**
 * Notes API functions
 */

import { apiClient, apiUpload, buildQueryString, PaginatedResponse } from "@/shared/lib/api/client";
import type {
  Note,
  NoteListItem,
  NoteWithRelations,
  NoteCreateInput,
  NoteUpdateInput,
  NoteCreateResponse,
} from "../types";

const BASE_PATH = "/api/notes";

/**
 * Metadata for a single note_type row. Returned by the unified
 * ``/api/note-types`` endpoint — single source of truth for what kinds
 * exist, how many notes are of each, and their display metadata.
 *
 * Replaces the old ``/api/notes/kinds`` endpoint which returned just
 * ``{note_kind, count}``. See plan ``alright-it-is-time-swift-bengio.md``.
 */
export interface NoteKindInfo {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  icon: string | null;              // icon slug (kebab-case)
  color: string | null;             // Tailwind color family
  note_count: number;               // live-computed on read
  allows_children: boolean;
  short_title: string | null;
  prefilled_values: Record<string, unknown> | null;
  parent_type_id: string | null;
}

interface NoteTypeListPage {
  items: NoteKindInfo[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * Fetch all note types plus their live note counts. Empty types
 * (``note_count === 0``) are filtered client-side so the dropdown /
 * sidebar only show kinds the user can actually filter by.
 */
export async function getNoteKinds(): Promise<NoteKindInfo[]> {
  const page = await apiClient<NoteTypeListPage>(`/api/note-types?limit=100`);
  return page.items.filter((k) => k.note_count > 0);
}

/**
 * Composable list filter — matches the backend's NoteFilters. Every
 * field AND-joins; multi-value fields OR within themselves.
 */
export interface NoteListFilters {
  skip?: number;
  limit?: number;
  type?: string[];          // note_type slugs to include
  type_not?: string[];      // note_type slugs to exclude
  root_only?: boolean;      // count(->part_of) = 0
  has_parent?: boolean;     // count(->part_of) > 0   (mutually exclusive w/ root_only)
  parent?: string;          // direct parent note id
  category?: string;        // filed under this category
  content_type?: string[];  // content-type values to include
  tags?: string[];          // ANY-match
  search?: string;
  semantic?: boolean;       // default true when search set
  sort?: "created_at" | "updated_at" | "title";
  sort_dir?: "ASC" | "DESC";
}

export async function getNotes(
  filter?: NoteListFilters,
): Promise<PaginatedResponse<NoteListItem>> {
  const query = buildQueryString({
    skip: filter?.skip ?? 0,
    limit: filter?.limit ?? 20,
    type: filter?.type,
    type_not: filter?.type_not,
    root_only: filter?.root_only,
    has_parent: filter?.has_parent,
    parent: filter?.parent,
    category: filter?.category,
    content_type: filter?.content_type,
    tags: filter?.tags,
    search: filter?.search,
    semantic: filter?.semantic,
    sort: filter?.sort ?? "created_at",
    sort_dir: filter?.sort_dir ?? "DESC",
  });
  return apiClient<PaginatedResponse<NoteListItem>>(`${BASE_PATH}${query}`);
}

export async function getNote(noteId: string): Promise<Note> {
  return apiClient<Note>(`${BASE_PATH}/${noteId}`);
}

export async function getNoteWithRelations(noteId: string): Promise<NoteWithRelations> {
  return apiClient<NoteWithRelations>(`${BASE_PATH}/${noteId}/full`);
}

export async function createNote(data: NoteCreateInput): Promise<NoteCreateResponse> {
  return apiClient<NoteCreateResponse>(BASE_PATH, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateNote(
  noteId: string,
  data: NoteUpdateInput,
  createVersion = true,
  options?: { signal?: AbortSignal; expectedOwnerId?: string },
): Promise<Note> {
  const query = buildQueryString({ create_version: createVersion });
  return apiClient<Note>(`${BASE_PATH}/${noteId}${query}`, {
    method: "PATCH",
    body: JSON.stringify(data),
    ...options,
  });
}

export async function deleteNote(noteId: string): Promise<void> {
  return apiClient<void>(`${BASE_PATH}/${noteId}`, {
    method: "DELETE",
  });
}

export async function getSimilarNotes(
  noteId: string,
  limit = 10
): Promise<NoteListItem[]> {
  const query = buildQueryString({ limit });
  return apiClient<NoteListItem[]>(`${BASE_PATH}/${noteId}/similar${query}`);
}

export async function addNoteCategory(
  noteId: string,
  categoryId: string
): Promise<void> {
  await apiClient<void>(`${BASE_PATH}/${noteId}/categories/${categoryId}`, {
    method: "POST",
  });
}

export async function removeNoteCategory(
  noteId: string,
  categoryId: string
): Promise<void> {
  await apiClient<void>(`${BASE_PATH}/${noteId}/categories/${categoryId}`, {
    method: "DELETE",
  });
}

export interface DuplicateCheckResult {
  duplicates: Array<{
    id: string;
    title: string;
    similarity: number;
    preview?: string;
  }>;
  has_duplicates: boolean;
  threshold: number;
  error?: string;
}

export async function checkNoteDuplicates(params: {
  title?: string;
  content?: string;
  threshold?: number;
}): Promise<DuplicateCheckResult> {
  const query = buildQueryString({
    title: params.title,
    content: params.content,
    threshold: params.threshold ?? 0.85,
  });
  return apiClient<DuplicateCheckResult>(`${BASE_PATH}/check-duplicates${query}`, {
    method: "POST",
  });
}

// ===================
// File Upload API
// ===================

export interface NoteFileUploadInput {
  file: File;
  title?: string;
  description?: string;
  tags?: string[];
  categoryIds?: string[];
  autoCategorize?: boolean;
  transcribe?: boolean;
  parentNoteIds?: string[];
}

export interface NoteFileUploadResponse extends NoteCreateResponse {
  content_type: string;
  processing_status: string;
  transcription?: string;
}

export async function uploadNoteFile(
  input: NoteFileUploadInput
): Promise<NoteFileUploadResponse> {
  const formData = new FormData();
  formData.append("file", input.file);
  
  if (input.title) formData.append("title", input.title);
  if (input.description) formData.append("description", input.description);
  if (input.tags?.length) formData.append("tags", input.tags.join(","));
  if (input.categoryIds?.length) formData.append("category_ids", input.categoryIds.join(","));
  if (input.autoCategorize !== undefined) formData.append("auto_categorize", String(input.autoCategorize));
  if (input.transcribe !== undefined) formData.append("transcribe", String(input.transcribe));
  if (input.parentNoteIds?.length) formData.append("parent_note_ids", input.parentNoteIds.join(","));

  return apiUpload<NoteFileUploadResponse>(`${BASE_PATH}/upload`, formData);
}

export interface NoteUrlUploadInput {
  url: string;
  title?: string;
  description?: string;
  tags?: string[];
  categoryIds?: string[];
  autoCategorize?: boolean;
  parentNoteIds?: string[];
}

export interface NoteUrlUploadResponse extends NoteCreateResponse {
  content_type: string;
  processing_status: string;
  source_url: string;
}

export async function uploadNoteFromUrl(
  input: NoteUrlUploadInput
): Promise<NoteUrlUploadResponse> {
  const formData = new FormData();
  formData.append("url", input.url);
  
  if (input.title) formData.append("title", input.title);
  if (input.description) formData.append("description", input.description);
  if (input.tags?.length) formData.append("tags", input.tags.join(","));
  if (input.categoryIds?.length) formData.append("category_ids", input.categoryIds.join(","));
  if (input.autoCategorize !== undefined) formData.append("auto_categorize", String(input.autoCategorize));
  if (input.parentNoteIds?.length) formData.append("parent_note_ids", input.parentNoteIds.join(","));

  return apiUpload<NoteUrlUploadResponse>(`${BASE_PATH}/upload-url`, formData);
}
